// What the 1-2-1's pages read and write (docs/data-model.md 4.6, 8.2 and 10.3), as the person
// logged in, through row level security:
// - the client reads their own open space, its sessions, the notes Adam has shown, the actions, the
//   files not removed, and ticks only their actions' done_at (guard_member_columns), and keeps their
//   place in each session's recording (hub.client_recording_progress, stage 5);
// - an owner or admin at 2-step login reads any space and writes its booking link, next session,
//   actions and session notes directly (policy 8.2, proved by pgTAP 250);
// - files come and go only through sign-file and sign-upload, and opening or ending a space only
//   through client-space (owner only);
// - while staff view as a member nothing is written: the screens don't call any of these writes,
//   and the database and functions refuse them anyway (writer_person_id() is null).
import { supabase, must } from '../../lib/supabase.js'
import { callHubFunction } from '../../lib/functions.js'
import { askAdam } from '../../product/data.js'
import { SPACE_COLUMNS, SESSION_COLUMNS, NOTE_COLUMNS, ACTION_COLUMNS, FILE_COLUMNS, PERSON_COLUMNS, PROGRESS_COLUMNS } from './columns.js'

const q = (table, columns) => supabase.from(table).select(columns)

// 1 space and everything in it. client: the client's own people row for the client (the member's
// own), else read here for Adam's view. progress: the client's place in each session's recording, read
// on the client's page only (a staff member viewing as the client reads the client's); a read that
// fails leaves it empty, so the page still opens. Null when the space isn't open to them, or isn't on
// productId, the product whose pages ask (each private client space product's spaces stay apart: the
// product-types plan, 13 Oct). The same request as before; the product is checked here, after it.
export async function loadSpace(spaceId, { staff = false, productId = null } = {}) {
  const spaces = await q('client_spaces', SPACE_COLUMNS).eq('id', spaceId).then(must)
  const space = spaces[0] || null
  if (!space) return null
  if (productId && space.product_id !== productId) return null
  const [sessions, actions, files, people] = await Promise.all([
    q('client_sessions', SESSION_COLUMNS).eq('space_id', spaceId).order('number').then(must),
    q('client_actions', ACTION_COLUMNS).eq('space_id', spaceId).is('archived_at', null)
      .order('sort_order').order('created_at').order('id').then(must),
    q('client_files', FILE_COLUMNS).eq('space_id', spaceId).is('removed_at', null)
      .order('created_at', { ascending: false }).order('id').then(must),
    staff ? q('people', PERSON_COLUMNS).eq('id', space.client_person_id).then(must) : Promise.resolve([]),
  ])
  const ids = sessions.map(s => s.id)
  const [notes, progress] = await Promise.all([
    ids.length ? q('client_session_notes', NOTE_COLUMNS).in('session_id', ids).order('session_id').then(must) : [],
    !staff && ids.length
      ? q('client_recording_progress', PROGRESS_COLUMNS).eq('person_id', space.client_person_id).in('session_id', ids).order('session_id')
        .then(r => (r.error ? [] : r.data || []))
      : [],
  ])
  return { space, sessions, notes, actions, files, progress, client: staff ? people[0] || null : null }
}

// Every space on 1 product, for Adam (owner or admin at 2-step login): the newest first, with each
// client's name. The same request as before, every space read, then only productId's kept.
export async function loadSpaces(productId = null) {
  const all = await q('client_spaces', SPACE_COLUMNS).order('opened_at', { ascending: false }).order('id').then(must)
  const spaces = productId ? all.filter(s => s.product_id === productId) : all
  const ids = [...new Set(spaces.map(s => s.client_person_id))].sort()
  const people = ids.length ? await q('people', PERSON_COLUMNS).in('id', ids).order('id').then(must) : []
  const byId = new Map(people.map(p => [p.id, p]))
  return spaces.map(s => ({ ...s, client: byId.get(s.client_person_id) || null }))
}

// ---- The client's writes

// Tick or untick 1 action (the client changes done_at only)
export async function tickAction(actionId, done) {
  return must(await supabase.from('client_actions').update({ done_at: done ? new Date().toISOString() : null }).eq('id', actionId))
}

// Ask Adam: a question with a link to the space (questions_insert, the client of an open space)
export function askInSpace({ personId, spaceId, contextPath, body }) {
  return askAdam({ person_id: personId, client_space_id: spaceId, context_path: contextPath, body })
}

// Where the client is in a session's recording: the client's own row (never while staff view as
// them). The database keeps furthest and seconds watched only growing, caps them at the recording's
// length and sets watched_at itself at the 1-2-1's watched share (merge_client_recording_progress).
export async function saveClientProgress(personId, sessionId, { position, furthest, watched }) {
  const n = v => Math.max(0, Math.floor(Number(v) || 0))
  const rows = await supabase.from('client_recording_progress').upsert({
    person_id: personId, session_id: sessionId,
    position_seconds: n(position), furthest_seconds: n(furthest), seconds_watched: n(watched),
  }, { onConflict: 'person_id,session_id' }).select(PROGRESS_COLUMNS).then(must)
  return rows?.[0] || null
}

// ---- Adam's writes (owner or admin at 2-step login)

export async function saveBookingUrl(spaceId, url) {
  return must(await supabase.from('client_spaces').update({ booking_url: url }).eq('id', spaceId))
}

// Add the next session (number: the space's highest plus 1; if 2 tabs race, the next one along), or
// change the 1 still to come
export async function saveNextSession({ spaceId, existing, startsAt, minutes, zoom, highest }) {
  const fields = { starts_at: startsAt, duration_minutes: minutes, zoom_join_url: zoom || null }
  if (existing) {
    const rows = await supabase.from('client_sessions').update(fields).eq('id', existing.id).select(SESSION_COLUMNS).then(must)
    return rows[0]
  }
  let number = (highest || 0) + 1
  for (let tries = 0; tries < 3; tries++) {
    const { data, error } = await supabase.from('client_sessions')
      .insert({ space_id: spaceId, number, title: `Session ${number}`, ...fields }).select(SESSION_COLUMNS)
    if (!error) return data[0]
    if (error.code !== '23505') throw error
    number += 1
  }
  throw new Error('No session number free')
}

export async function addAction({ spaceId, agreedSessionId, body, sortOrder, createdBy }) {
  const rows = await supabase.from('client_actions').insert({
    space_id: spaceId, agreed_session_id: agreedSessionId, body, sort_order: sortOrder, created_by: createdBy,
  }).select(ACTION_COLUMNS).then(must)
  return rows[0]
}

// "Save and show Laura": the recording's link and the notes, shown to the client now
export async function saveNotes({ sessionId, recordingUrl, notes }) {
  const rows = await supabase.from('client_session_notes').upsert({
    session_id: sessionId, recording_url: recordingUrl, notes: notes || null, shown_at: new Date().toISOString(),
  }, { onConflict: 'session_id' }).select(NOTE_COLUMNS).then(must)
  return rows[0]
}

// End a space (client-space, owner only, audited as client_space_ended)
export function endSpace(spaceId) {
  return callHubFunction('client-space', { action: 'end', space_id: spaceId })
}

// Open a space (client-space, owner only, audited as client_space_opened); a new person needs a name
export function openSpace({ email, fullName, bookingUrl }) {
  const body = { action: 'open', email }
  if (fullName && fullName.trim()) body.full_name = fullName.trim()
  if (bookingUrl && bookingUrl.trim()) body.booking_url = bookingUrl.trim()
  return callHubFunction('client-space', body)
}

// ---- Files, both ways

// A file's link, signed for 2 minutes (sign-file's client_file_id branch)
export function signClientFile(fileId) {
  return callHubFunction('sign-file', { client_file_id: fileId })
}

// Add a file: sign-upload gives a 1-off upload link, the file goes straight to private storage, then
// sign-upload writes the client_files row once the file is there
export async function uploadClientFile(spaceId, file, contentType) {
  const start = await callHubFunction('sign-upload', {
    action: 'start', space_id: spaceId, file_name: file.name, content_type: contentType, size_bytes: file.size,
  })
  const { error } = await supabase.storage.from(start.bucket || 'client-files')
    .uploadToSignedUrl(start.path, start.token, file, { contentType })
  if (error) throw error
  const done = await callHubFunction('sign-upload', { action: 'finish', receipt: start.receipt })
  return done.file
}
